import { QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  ensureApplicationIdPersisted,
  initApplicationId,
  persistNativeApplicationId,
} from "./lib/applicationId";
import { applyDocumentLanguage } from "./lib/language";
import { queryClient } from "./lib/queryClient";
import { initSentry } from "./lib/sentry/instrument";
import { bootstrapTheme } from "./lib/theme";
import { trackFirebaseScreenViewForPath } from "./lib/tracking/firebase";
import { routeTree } from "./routeTree.gen";
import "./index.css";

// 初回ペイントから正しいテーマカラーで描画するため、render 前に localStorage の
// application_id を確定させてからテーマを :root に書き込む（Utage-Web 運用と同じ）。
if (typeof window !== "undefined") {
  persistNativeApplicationId(new URLSearchParams(window.location.search));
  ensureApplicationIdPersisted();
}
bootstrapTheme();

// プレビュー(PRごと)は /pr-<番号>/ 配下で動くため、ルーターの basepath を
// ビルドの base(import.meta.env.BASE_URL) に合わせる。末尾スラッシュは除去し、
// 本番は "/"→"" → undefined ＝無指定（従来どおり）。
const routerBasepath = import.meta.env.BASE_URL.replace(/\/$/, "");
const router = createRouter({
  routeTree,
  basepath: routerBasepath || undefined,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

initSentry(router);

// Firebase Analytics の screen_view を全ルート遷移で一括送信する（各画面に手書きしない）
router.subscribe("onResolved", ({ toLocation }) => {
  const pathname =
    routerBasepath && toLocation.pathname.startsWith(routerBasepath)
      ? toLocation.pathname.slice(routerBasepath.length)
      : toLocation.pathname;
  trackFirebaseScreenViewForPath(pathname || "/");
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}

await initApplicationId(new URLSearchParams(window.location.search));
// sina は国内専用のため ja 固定で document の言語・書字方向を設定する
applyDocumentLanguage("ja");

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  </StrictMode>,
);
